import type { SortingState } from "@tanstack/react-table";
import { useCallback, useRef, useState } from "react";

import { changesAny } from "./viewChange";

// What the Workshop's Tables tab shows; the toolbar, the header and Genie's
// list tool all change it through ``tablesListReducer``.
export interface TablesListView {
  search: string;
  tagIds: string[];
  sorting: SortingState;
  page: number;
  pageSize: number;
}

const RESETS_PAGE: (keyof TablesListView)[] = ["search", "tagIds", "pageSize"];

// A different set of tables, or a different page size, starts again at page
// 1 unless the same change asks for a page. A new sort keeps the page.
export function tablesListReducer(
  view: TablesListView,
  patch: Partial<TablesListView>
): TablesListView {
  const next = { ...view, ...patch };
  if (changesAny(view, patch, RESETS_PAGE) && patch.page === undefined) {
    next.page = 1;
  }
  return next;
}

// The reducer whose current state can also be read outside render: Genie's
// list tool changes the view and counts against it in the same step.
export function useTablesListState(init: () => TablesListView) {
  const [view, setView] = useState(init);
  const latest = useRef(view);
  const change = useCallback((patch: Partial<TablesListView>) => {
    latest.current = tablesListReducer(latest.current, patch);
    setView(latest.current);
  }, []);
  const read = useCallback(() => latest.current, []);
  return [view, change, read] as const;
}
